import { getEnvironmentConfig } from 'utils/environment';
import { CLIENT_SOURCE_WEB } from './request-headers';

export type ReportClientErrorPayload = {
  message: string;
  stack?: string | null;
  currentUrl?: string | null;
  apiPath?: string | null;
  payloadSnippet?: string | null;
  /** Defaults to `'web'`; matches backend `ReportClientErrorInput.clientSource`. */
  clientSource?: string;
  /** Overrides sessionStorage-derived email when supplied. */
  reportedEmail?: string | null;
};

/** POST /observability/client-errors (matches backend Joi `ReportClientErrorInput`). */
const OBSERVABILITY_PATH = 'observability/client-errors';

const SCRIPT_ERROR_PATTERN = /^script error\.?$/i;

const CRAWLER_USER_AGENT_PATTERN =
  /bytespider|googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebookexternalhit|linkedinbot|twitterbot|semrushbot|ahrefsbot|petalbot|applebot|gptbot|claudebot|anthropic-ai|ccbot/i;

/** Cross-origin script failures and crawler traffic are not actionable in our logs. */
function isNonActionableClientError(message: string, userAgent: string): boolean {
  if (SCRIPT_ERROR_PATTERN.test(message.trim())) {
    return true;
  }

  return CRAWLER_USER_AGENT_PATTERN.test(userAgent);
}

function stripSecrets(text: string): string {
  return text
    .replace(/\bBearer\s+[A-Za-z0-9._\-]+\b/gi, 'Bearer [redacted]')
    .replace(
      /\b(accessToken|idToken|refreshToken|password|secret|apiKey)\b\s*[:=]\s*("?)[^"\s',}]+\2/gi,
      '$1=[redacted]'
    )
    .replace(/\b(pk_live_|pk_test_|sk_live_|sk_test_)[^\s"',}]+/gi, '$1[redacted]');
}

function truncate(s: string, max: number): string {
  const t = stripSecrets(s);
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

function readOptionalUserId(): number | null {
  try {
    const raw = window.localStorage.getItem('userId');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/** Session set when `GET /auth/me` succeeds (`auth.api.ts`). */
function readOptionalUserEmail(): string | null {
  try {
    const email = window.sessionStorage.getItem('userEmail');
    const trimmed = email?.trim();
    if (!trimmed || trimmed.length > 254) return null;
    return trimmed;
  } catch {
    return null;
  }
}

/**
 * Sends a sanitized client-side error payload to the API for structured logging → CloudWatch.
 * Fire-and-forget; skips nested calls / obvious loops.
 */
export async function reportClientError(payload: ReportClientErrorPayload): Promise<void> {
  if (typeof window === 'undefined') return;
  const { apiUrl } = getEnvironmentConfig();
  if (!apiUrl) return;

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (isNonActionableClientError(payload.message || '', userAgent)) return;

  const combined = `${payload.message}${payload.stack ?? ''}${payload.apiPath ?? ''}`;
  if (/observability\/client-errors/i.test(combined)) return;

  const message = truncate(payload.message || 'client-error', 4000);

  const currentUrlResolved =
    payload.currentUrl ??
    (typeof window !== 'undefined' ? truncate(window.location.href, 4000) : null);

  const reportedEmailResolved =
    payload.reportedEmail != null && payload.reportedEmail.trim() !== ''
      ? truncate(payload.reportedEmail.trim(), 255)
      : readOptionalUserEmail();

  const body = {
    message,
    stack: payload.stack ? truncate(payload.stack, 16000) : undefined,
    currentUrl: currentUrlResolved || null,
    apiPath: payload.apiPath ? truncate(payload.apiPath, 2000) : null,
    clientSource: payload.clientSource ?? CLIENT_SOURCE_WEB,
    userAgent: userAgent ? truncate(userAgent, 1024) : null,
    reportedUserId: readOptionalUserId(),
    reportedEmail: reportedEmailResolved || null,
    payloadSnippet: payload.payloadSnippet ? truncate(payload.payloadSnippet, 4000) : null,
  };

  const base = apiUrl.replace(/\/+$/, '');
  const url = `${base}/${OBSERVABILITY_PATH}`;

  let auth = '';
  let companyId = '';
  try {
    auth = window.localStorage.getItem('Authorization') ?? '';
    companyId = window.localStorage.getItem('company-id') ?? '';
  } catch {
    // ignore storage access errors
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-client-source': body.clientSource,
    'x-client-platform': 'WEB',
    'x-request-id':
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  };
  if (auth) headers.authorization = auth;
  if (companyId) headers['company-id'] = companyId;

  try {
    await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'omit',
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // swallow — reporting must never break UX
  }
}
